/* eslint-disable camelcase */
import { hospitalityRoles, hospitalityTypes, industryRoles, industryTypes } from '../consts';

type TErrorMessage = {
    response: {
        data: {
            meta: {
                detail: {
                    non_field_errors: string[];
                    date: string[];
                    agency: string[];
                    duration: {
                        duration: string;
                    };
                    breaks: string[];
                    consultant_splits: string[] | {agent?: string[], split_value?: string[]}[];
                    title: string[];
                };
            };
        };
    };
};

export function replaceRoleShortcutWithFullRoleName(text: string) {
    let replacedText = text;

    const hospitalityTypesList = Object.entries(hospitalityTypes);

    hospitalityTypesList.forEach(type => {
        replacedText = replacedText.replace(
            ` ${type[1].toLowerCase()} `,
            ` ${hospitalityRoles[type[0] as keyof typeof hospitalityRoles]} `,
        );
    });

    const industryTypesList = Object.entries(industryTypes);

    industryTypesList.forEach(type => {
        replacedText = replacedText.replace(
            ` ${type[1].toLowerCase()} `,
            ` ${industryRoles[type[0] as keyof typeof industryRoles]} `,
        );
    });

    return replacedText;
}

const getUniqueErrors = (array: any[]) => array.filter(
    (item, index) => array.indexOf(item) === index,
);


export function getErrorMessages(error: TErrorMessage): string {
    if (!error.response || !error.response.data) {
        return 'Network error, please check your connection';
    }

    const errorDetail = error?.response?.data?.meta?.detail;

    if (typeof errorDetail === 'string') {
        return errorDetail;
    }

    if (errorDetail?.non_field_errors) {
        return getUniqueErrors(errorDetail.non_field_errors).join(', ');
    }

    if (errorDetail?.breaks) {
        return getUniqueErrors(errorDetail.breaks).join(', ').replace(
            'this',
            'Break'
        );
    }

    if (errorDetail?.consultant_splits) {
        const firstError = errorDetail.consultant_splits[0];
        if (typeof firstError === 'string') {
            return firstError;
        }
        return Object.values(firstError)[0].join(', ');
    }

    if (errorDetail?.duration) {
        if (Array.isArray(errorDetail?.duration)) {
            return Object.values(errorDetail.duration)[0]?.duration;
        }
        return errorDetail.duration?.duration;
    }

    if (errorDetail?.title) {
        if (Array.isArray(errorDetail?.title)) {
            return Object.values(errorDetail.title)[0].replace(
                'this',
                'Job Title',
            );
        }
    }

    if (errorDetail) {
        const firstValueErrorDetail = Object.values(errorDetail)[0];

        if (Array.isArray(firstValueErrorDetail)) {
            const errors = getUniqueErrors(firstValueErrorDetail);
            return errors.map((err, i) =>
                typeof err === 'string' ? err : JSON.stringify(errors[i])
            ).join(', ');
        }
    }

    return 'Unknown error has occurred.';
}

function fieldLabel(field: string): string {
    return field
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function collectMessages(value: unknown): string[] {
    if (typeof value === 'string') {
        return [value];
    }
    if (Array.isArray(value)) {
        return value.flatMap(item => collectMessages(item));
    }
    if (value && typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).flatMap(item =>
            collectMessages(item),
        );
    }
    return [];
}

/**
 * Same sources as getErrorMessages, but field errors keep the field name.
 * "This parameter is required" on its own does not say which parameter failed.
 */
export function getErrorMessagesWithFieldNames(error: TErrorMessage): string {
    const errorDetail = error?.response?.data?.meta?.detail;

    if (!errorDetail || typeof errorDetail !== 'object' || Array.isArray(errorDetail)) {
        return getErrorMessages(error);
    }

    const parts = Object.entries(errorDetail).flatMap(([field, value]) => {
        const messages = getUniqueErrors(collectMessages(value));
        if (messages.length === 0) {
            return [];
        }
        if (field === 'non_field_errors') {
            return [messages.join(', ')];
        }
        return [`${fieldLabel(field)}: ${messages.join(', ')}`];
    });

    if (parts.length === 0) {
        return getErrorMessages(error);
    }

    return parts.join('. ');
}
